// src/app/shared/components/receipt/receipt.component.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrintService } from '../../../core/services/print.service';
import { effect, signal } from '@angular/core';
import * as QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (printService.currentPrintData(); as printData) {
      @if (printData.printKitchenTicket) {
        <div class="receipt-container page-break-after">
          <div class="receipt-header">
            <h1 class="cafe-name">KITCHEN TICKET</h1>
            <div class="divider"></div>
            
            <div class="meta-data">
              <div class="order-type-header" style="text-align: center; font-weight: bold; margin-bottom: 4px; font-size: 14px;">
                *** {{ printData.bill.type === 'TAKEAWAY' ? 'TAKEAWAY' : 'DINE-IN' }} ***
              </div>
              <div class="meta-row" style="font-size: 14px; font-weight: bold; margin-bottom: 6px;">
                <span>Order No:</span>
                <span>#{{ printData.bill.orderNumber }}</span>
              </div>
              <div class="meta-row">
                <span>Date:</span>
                <span>{{ printData.bill.generatedAt | date:'short' }}</span>
              </div>
              @if (printData.bill.tokenNumber) {
                <div class="meta-row" style="font-size: 16px; font-weight: 900; margin-top: 6px; text-align: center; justify-content: center;">
                  <span>TOKEN: {{ printData.bill.tokenNumber }}</span>
                </div>
              }
              @if (printData.bill.type !== 'TAKEAWAY' && printData.bill.tableNumber) {
                <div class="meta-row" style="font-size: 14px; font-weight: bold; margin-top: 6px;">
                  <span>Table:</span>
                  <span>{{ printData.bill.tableNumber }}</span>
                </div>
              }
            </div>
            <div class="divider"></div>
          </div>

          <div class="receipt-body">
            @for (item of printData.bill.items; track item.id) {
              <div style="margin-bottom: 8px; font-size: 14px; font-weight: bold;">
                {{ item.quantity }}x {{ item.menuItemName }}
                @if (item.variantName) {
                  [{{ item.variantName }}]
                }
                @if (item.addons && item.addons.length > 0) {
                  @for (addon of item.addons; track addon.addonId) {
                    <div style="font-size: 12px; font-weight: normal; padding-left: 12px; margin-top: 2px;">
                      + {{ addon.quantity }}x {{ addon.name }}
                    </div>
                  }
                }
                @if (item.notes) {
                  <div style="font-size: 12px; font-weight: normal; padding-left: 12px; margin-top: 2px;">
                    -> {{ item.notes }}
                  </div>
                }
              </div>
            }
          </div>
          <div class="divider"></div>
          <br><br>
        </div>
      }

      <div class="receipt-container">
        <!-- Receipt Header -->
        <div class="receipt-header">
          @if (printData.logoUrl) {
            <div class="text-center mb-2 flex justify-center">
              <img [src]="printData.logoUrl" alt="Logo" class="receipt-logo" />
            </div>
          }
          <h1 class="cafe-name">{{ printData.bill.cafeName || 'Selquna' }}</h1>
          <p class="receipt-info whitespace-pre-wrap">{{ printData.cafeAddress }}</p>
          <div class="divider"></div>
          
          <div class="meta-data">
            <div class="order-type-header" style="text-align: center; font-weight: bold; margin-bottom: 4px; font-size: 14px;">
              *** {{ printData.bill.type === 'TAKEAWAY' ? 'TAKEAWAY' : 'DINE-IN' }} ***
            </div>
            <div class="meta-row">
              <span>Order No:</span>
              <span>#{{ printData.bill.orderNumber }}</span>
            </div>
            <div class="meta-row">
              <span>Date:</span>
              <span>{{ printData.bill.generatedAt | date:'short' }}</span>
            </div>
            @if (printData.bill.tokenNumber) {
              <div class="meta-row" style="font-size: 16px; font-weight: 900; margin-top: 6px; text-align: center; justify-content: center;">
                <span>TOKEN: {{ printData.bill.tokenNumber }}</span>
              </div>
            }
            @if (printData.bill.type !== 'TAKEAWAY' && printData.bill.tableNumber) {
              <div class="meta-row">
                <span>Table:</span>
                <span>{{ printData.bill.tableNumber }}</span>
              </div>
            }
          </div>
          <div class="divider"></div>
        </div>

        <!-- Receipt Body (Items) -->
        <div class="receipt-body">
          <table class="items-table">
            <thead>
              <tr>
                <th class="qty-col">Qty</th>
                <th class="item-col">Item</th>
                <th class="price-col">Amt</th>
              </tr>
            </thead>
            <tbody>
              @for (item of printData.bill.items; track item.id) {
                <tr>
                  <td class="qty-col">{{ item.quantity }}x</td>
                  <td class="item-col">
                    {{ item.menuItemName }}
                    @if (item.variantName) {
                      <br>
                      <span style="font-size: 10px; color: #555;">[{{ item.variantName }}]</span>
                    }
                    @if (item.addons && item.addons.length > 0) {
                      @for (addon of item.addons; track addon.addonId) {
                        <br>
                        <span style="font-size: 10px; color: #555;">+ {{ addon.quantity }}x {{ addon.name }} (&#64; {{ addon.price | currency:'INR':'symbol-narrow':'1.0-0' }})</span>
                      }
                    }
                    <br>
                    <span style="font-size: 8px; color: #555;">&#64; {{ item.unitPrice | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
                  </td>
                  <td class="price-col">{{ item.totalPrice | currency:'INR':'symbol-narrow':'1.0-0' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="divider"></div>

        <!-- Receipt Footer (Totals) -->
        <div class="receipt-footer">
          <div class="totals-row">
            <span>Subtotal:</span>
            <span>{{ printData.bill.subtotal | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
          </div>
          @if (printData.bill.serviceCharge && printData.bill.serviceCharge > 0) {
            <div class="totals-row">
              <span>Service Charge{{ printData.bill.serviceChargePercentage ? ' (' + printData.bill.serviceChargePercentage + '%)' : '' }}:</span>
              <span>{{ printData.bill.serviceCharge | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
            </div>
          }
          @if (printData.bill.deliveryCharge && printData.bill.deliveryCharge > 0) {
            <div class="totals-row">
              <span>Delivery Charge{{ printData.bill.deliveryChargePercentage ? ' (' + printData.bill.deliveryChargePercentage + '%)' : '' }}:</span>
              <span>{{ printData.bill.deliveryCharge | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
            </div>
          }
          @if (printData.bill.discountAmount > 0) {
            <div class="totals-row discount">
              <span>Discount{{ printData.bill.discountPercentage ? ' (' + printData.bill.discountPercentage + '%)' : '' }}:</span>
              <span>-{{ printData.bill.discountAmount | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
            </div>
          }
          <div class="totals-row">
            <span>Tax{{ printData.bill.taxPercentage ? ' (' + printData.bill.taxPercentage + '%)' : ' (GST)' }}:</span>
            <span>{{ printData.bill.taxAmount | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
          </div>
          <div class="divider"></div>
          <div class="totals-row grand-total">
            <span>TOTAL:</span>
            <span>{{ printData.bill.total | currency:'INR':'symbol-narrow':'1.0-0' }}</span>
          </div>
        </div>

        <!-- Outro -->
        <div class="receipt-outro">
          <p class="whitespace-pre-wrap">{{ printData.receiptFooter }}</p>
          
          @if (codeUrl()) {
            <div class="code-container">
              <img [src]="codeUrl()" alt="Receipt Code" class="receipt-code" />
            </div>
          }

          <p class="powered-by">Powered by Selquna POS</p>
        </div>
      </div>
    }
  `,
  styles: [`
    /* Only render this component when printing */
    @media screen {
      :host {
        display: none !important;
      }
    }

    @media print {
      :host {
        display: block;
        width: 80mm;
        margin: 0;
        padding: 0;
        color: #000;
        font-family: 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.2;
      }

      .receipt-container {
        width: 100%;
        padding: 2mm;
      }

      .page-break-after {
        page-break-after: always;
      }

      .cafe-name {
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        margin: 0 0 4px 0;
        text-transform: uppercase;
      }

      .receipt-logo {
        max-width: 60px;
        max-height: 60px;
        object-fit: contain;
        display: block;
        margin: 0 auto;
      }

      .receipt-info {
        text-align: center;
        font-size: 10px;
        margin: 0 0 8px 0;
      }

      .divider {
        border-bottom: 1px dashed #000;
        margin: 8px 0;
      }

      .meta-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 2px;
      }

      .items-table {
        width: 100%;
        border-collapse: collapse;
      }

      .items-table th, .items-table td {
        text-align: left;
        vertical-align: top;
        padding: 2px 0;
      }

      .items-table .price-col {
        text-align: right;
      }
      
      .items-table .qty-col {
        width: 25px;
      }

      .items-table th {
        border-bottom: 1px solid #000;
        padding-bottom: 4px;
        margin-bottom: 4px;
      }

      .totals-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 4px;
      }

      .totals-row.grand-total {
        font-size: 16px;
        font-weight: bold;
      }

      .receipt-outro {
        text-align: center;
        margin-top: 16px;
      }

      .code-container {
        display: flex;
        justify-content: center;
        margin: 12px 0;
      }

      .receipt-code {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 0 auto;
      }

      .powered-by {
        font-size: 9px;
        margin-top: 4px;
      }
    }
  `]
})
export class ReceiptComponent {
  public printService = inject(PrintService);
  public codeUrl = signal<string | null>(null);

  constructor() {
    effect(() => {
      const data = this.printService.currentPrintData();
      if (data) {
        if (data.receiptCodeType === 'QRCODE') {
          QRCode.toDataURL(data.bill.orderNumber, { margin: 1, width: 100 })
            .then(url => this.codeUrl.set(url))
            .catch(err => console.error('Failed to generate QR', err));
        } else if (data.receiptCodeType === 'BARCODE' || !data.receiptCodeType) {
          try {
            const canvas = document.createElement('canvas');
            // @ts-ignore
            JsBarcode(canvas, data.bill.orderNumber, {
              format: 'CODE128',
              displayValue: true,
              height: 40,
              margin: 0,
              fontSize: 12
            });
            this.codeUrl.set(canvas.toDataURL('image/png'));
          } catch (e) {
            console.error('Failed to generate barcode', e);
          }
        } else {
          this.codeUrl.set(null);
        }
      } else {
        this.codeUrl.set(null);
      }
    }, { allowSignalWrites: true });
  }
}
// trigger recompile
